import moment from 'moment';
import { DATE_FORMATS } from 'utils/helpers';

// Practices define their own tiers, so nothing here assumes a fixed number of
// plans, households or intervals — everything is derived from the payload.
export const HOUSEHOLD_LABELS = {
  single: 'Single',
  couple: 'Couple',
  family: 'Family',
};

export const INTERVAL_LABELS = {
  year: 'Annual',
  month: 'Monthly',
};

const HOUSEHOLD_ORDER = ['single', 'couple', 'family'];
const INTERVAL_ORDER = ['year', 'month'];

export const LABEL = 'text-[10px] font-semibold uppercase tracking-widest text-secondary-400';

export const PRIMARY_PLAN_BTN =
  'btn h-11 min-h-11 rounded-xl border-0 bg-secondary-900 text-sm font-semibold text-white hover:bg-secondary-800';

export const OUTLINE_PLAN_BTN =
  'btn h-11 min-h-11 rounded-xl border border-secondary-200 bg-white text-sm font-semibold text-secondary-900 hover:border-secondary-300 hover:bg-secondary-50';

export const categoryFor = (plan, household) =>
  (plan?.categories || []).find((category) => category.household_type === household);

// `prices` only carries intervals that can be bought right now, so a missing
// key means "not for sale" — never "free".
export const priceFor = (plan, household, interval) =>
  categoryFor(plan, household)?.prices?.[interval];

export const householdOptions = (plans = []) => {
  const offered = new Set(
    plans.flatMap((plan) => (plan?.categories || []).map((category) => category.household_type))
  );

  return HOUSEHOLD_ORDER
    .filter((household) => offered.has(household))
    .map((key) => ({ key, label: HOUSEHOLD_LABELS[key] }));
};

export const intervalOptions = (plans = [], household) => {
  const offered = new Set(
    plans.flatMap((plan) => Object.keys(categoryFor(plan, household)?.prices || {}))
  );

  return INTERVAL_ORDER
    .filter((interval) => offered.has(interval))
    .map((key) => ({ key, label: INTERVAL_LABELS[key] }));
};

// A tier whose households and prices are all paused still arrives — the API
// sends every active tier, and members on a paused price need it to stay there
// for their own panel. So "does this practice sell anything?" has to be asked
// of the prices, never of plans.length.
export const hasSellablePlans = (plans = []) =>
  plans.some((plan) =>
    (plan?.categories || []).some((category) => Object.keys(category?.prices || {}).length > 0)
  );

export const visiblePlans = (plans = [], household, interval) =>
  plans.filter((plan) => priceFor(plan, household, interval) != null);

// Monthly and annual are priced independently, so the saving has to be measured
// rather than assumed. Quote it from the plan we lead with.
export const annualSavings = (plans = [], household) => {
  const comparable = plans.filter((plan) => {
    const { month, year } = categoryFor(plan, household)?.prices || {};
    return month != null && year != null;
  });

  const plan = comparable.find(({ most_popular }) => most_popular) ?? comparable[0];
  if (!plan) return null;

  const { month, year } = categoryFor(plan, household).prices;
  const saving = month * 12 - year;

  return saving > 0 ? saving : null;
};

// ponytail: enrollment carries plan_name but no plan id, so the discount can
// only be matched back by name. Switch to an id if the API ever exposes one.
export const discountFor = (plans = [], enrollment) =>
  plans.find(({ name }) => name === enrollment?.plan_name)?.treatment_discount_percent ?? null;

export const isLiveEnrollment = (enrollment) => !!enrollment && enrollment.state !== 'canceled';

// A checkout that was started and never paid for. The row exists and the Stripe
// invoice is open, but nothing has been collected — so it is not coverage, and
// showing "You're a member" for one is a lie the patient acts on.
export const isPendingCheckout = (enrollment) => enrollment?.state === 'pending';

// Mirrors Memberships::ChangePlan — only a dearer plan is charged for today.
// Anything else (cheaper, or the same price on another tier) waits for renewal.
export const isUpgrade = (nextPrice, currentPrice) => nextPrice > currentPrice;

// Every plan × household combination sold at this interval. One of them is the
// plan they're already on, so there is nothing to change to unless it's >1.
export const planOptionCount = (plans = [], interval) =>
  householdOptions(plans).reduce(
    (total, { key }) => total + visiblePlans(plans, key, interval).length,
    0
  );

// ── Who the membership covers ───────────────────────────────────────────────
// Mirrors Memberships::MINIMUM_MEMBERS on the API. A couple is priced for two,
// so it needs two; a family may leave seats empty — it's a flat price either
// way — but one person is a single plan, not a family one.
const MIN_SEATS = { single: 1, couple: 2, family: 2 };

export const minSeats = (household) => MIN_SEATS[household] ?? 1;

export const seatLimit = (plan, household) => categoryFor(plan, household)?.member_limit ?? 1;

// Single plans have nothing to choose, so they skip the picker entirely.
export const needsMemberPicking = (household) => minSeats(household) > 1;

export const canCheckout = (selectedIds = [], household, limit) =>
  selectedIds.length >= minSeats(household) && selectedIds.length <= limit;

// A patient with no guarantor has no family on file, so a couple/family plan
// can never be filled — say so rather than showing a one-row picker.
export const householdTooSmall = (people = [], household) => people.length < minSeats(household);

export const seatsLabel = (selectedCount, limit) =>
  `${selectedCount} of ${limit} ${limit === 1 ? 'seat' : 'seats'} filled`;

export const billedLabel = (interval) => (interval === 'year' ? 'Billed once a year' : 'Billed monthly');

export const billedTileLabel = (interval) => (interval === 'year' ? 'Billed yearly' : 'Billed monthly');

export const intervalSuffix = (interval) => (interval === 'year' ? '/ year' : '/ month');

export const coverageLabel = (interval) =>
  interval === 'year' ? 'Covers you for 12 months' : 'Renews every month';

// The API reports `pending` with a null current_period_end until the first
// payment clears, so straight after checkout the renewal date is ours to work out.
export const renewalDate = (enrollment) => {
  const { current_period_end, interval } = enrollment || {};

  if (current_period_end) return moment(current_period_end).format(DATE_FORMATS.medium);
  if (!interval) return null;

  return moment().add(1, interval).format(DATE_FORMATS.medium);
};

// Whole-dollar plan prices read better without cents ($349), but a four-figure
// family plan needs the separator ($2,189 — not $2189).
export const formatMoney = (amount) => {
  const value = Number(amount);
  const fractionDigits = Number.isInteger(value) ? 0 : 2;

  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

// ── Leaving ─────────────────────────────────────────────────────────────────
// Mirrors MembershipEnrollment.cancellation_reasons — the API rejects anything
// else, so the two lists have to stay in step.
export const CANCELLATION_REASONS = [
  { key: 'switching_to_insurance', label: "I'm getting dental insurance" },
  { key: 'too_expensive', label: "It's more than I want to spend" },
  { key: 'not_using_it', label: "I'm not using it" },
  { key: 'other', label: 'Something else' },
];

// Cancelling is scheduled, not immediate: `state` stays active and the patient
// keeps their benefits until this date, so the UI keys off the date, not state.
export const cancellationDate = (enrollment) =>
  enrollment?.cancels_at ? moment(enrollment.cancels_at).format(DATE_FORMATS.medium) : null;

// What the patient loses, and when. Falls back to the renewal date because a
// cancellation always lands on the end of the period already paid for.
export const coverageEndsOn = (enrollment) => cancellationDate(enrollment) ?? renewalDate(enrollment);
